import { useCallback, useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ExternalLink, MessageCircle } from "lucide-react";
import type { components } from "@dolfin/api-public";
import { useDolfinClient } from "@/components/DolfinProvider";
import { ErrorState, Eyebrow, LoadingState } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { formatWhatsAppNumber } from "@/lib/whatsapp";
import { apiErrorMessage, apiErrorType } from "@/lib/api-error";
import { formatDate } from "@/lib/format";

type Enrolment = components["schemas"]["WhatsAppEnrolmentResponse"];

const POLL_MS = 3000;

/** Reverse verification: the user sends a code to the Client's number from their WhatsApp, proving it and opting in. */
export function WhatsAppNotificationsCard() {
  const api = useDolfinClient();
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // The POST's answer: the only response that carries the code, so it is kept apart from later reads.
  const [pending, setPending] = useState<Enrolment | null>(null);
  const [notConnected, setNotConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await api.GET("/v1/me/whatsapp-enrolment");
    setLoadFailed(!!error);
    if (data) setEnrolment(data);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const expiresAt = pending?.codeExpiresAt ? new Date(pending.codeExpiresAt).getTime() : null;
  const waiting = !!pending?.code && expiresAt !== null && now < expiresAt;

  useEffect(() => {
    if (!waiting) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(async () => {
      const { data } = await api.GET("/v1/me/whatsapp-enrolment");
      if (data?.status === "Verified") {
        setEnrolment(data);
        setPending(null);
        toast.success("WhatsApp notifications are on.");
      }
    }, POLL_MS);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [api, waiting]);

  async function start() {
    if (busy) return;
    setBusy(true);
    setNotConnected(false);
    const { data, error } = await api.POST("/v1/me/whatsapp-enrolment");
    setBusy(false);
    if (error || !data) {
      if (apiErrorType(error).includes("WhatsApp.NotConnected")) setNotConnected(true);
      toast.error(apiErrorMessage(error, "Could not start WhatsApp notifications."));
      return;
    }
    setNow(Date.now());
    setPending(data);
    setEnrolment(data);
  }

  async function stop() {
    if (busy) return;
    setBusy(true);
    const { error } = await api.DELETE("/v1/me/whatsapp-enrolment");
    setBusy(false);
    if (error) {
      toast.error(apiErrorMessage(error, "Could not stop WhatsApp notifications."));
      return;
    }
    setEnrolment({ status: null });
    toast.success("WhatsApp notifications are off.");
  }

  if (loading && !enrolment) return <LoadingState label="Loading WhatsApp notifications..." />;
  if (loadFailed && !enrolment) {
    return <ErrorState message="Couldn't load your WhatsApp notifications." onRetry={() => void load()} />;
  }

  const status = enrolment?.status ?? null;
  const expired = !!pending?.code && !waiting;

  return (
    <section className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <div className="flex items-start gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-lg border bg-muted/50 text-muted-foreground">
          <MessageCircle className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <Eyebrow>WhatsApp notifications</Eyebrow>
          <p className="mt-1 text-sm text-muted-foreground text-pretty">
            Get a WhatsApp message when a bill needs your approval, with a link to review it.
          </p>
        </div>
      </div>

      <div className="mt-5">
        {status === "Verified" ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <p className="font-medium">
                On for <span className="font-mono">{formatWhatsAppNumber(enrolment?.phone)}</span>
              </p>
              {enrolment?.verifiedAt ? (
                <p className="text-xs text-muted-foreground">Since {formatDate(enrolment.verifiedAt)}</p>
              ) : null}
            </div>
            <Button variant="outline" size="sm" disabled={busy} onClick={stop}>
              {busy && <Spinner className="size-4" />}
              Stop WhatsApp notifications
            </Button>
          </div>
        ) : waiting && pending ? (
          <CodeStep enrolment={pending} secondsLeft={Math.ceil((expiresAt! - now) / 1000)} />
        ) : (
          <div className="space-y-3">
            {notConnected ? (
              <Notice tone="warn">
                WhatsApp notifications aren't set up for your account yet: the code goes to the main WhatsApp
                number, which hasn't been connected. Ask your administrator, then try again.
              </Notice>
            ) : expired ? (
              <Notice tone="warn">That code has expired. Get a new one to try again.</Notice>
            ) : status === "OptedOut" ? (
              <Notice>You replied STOP, so WhatsApp notifications are off. You can turn them on again.</Notice>
            ) : status === "Pending" ? (
              <Notice>Waiting for your code. If you no longer have it, get a new one.</Notice>
            ) : null}
            <Button size="sm" disabled={busy} onClick={start}>
              {busy && <Spinner className="size-4" />}
              {expired || status === "Pending" ? "Get a new code" : "Get notifications on WhatsApp"}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function CodeStep({ enrolment, secondsLeft }: { enrolment: Enrolment; secondsLeft: number }) {
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = String(secondsLeft % 60).padStart(2, "0");
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            Send this code to <span className="font-mono font-medium">{enrolment.sendTo}</span> from your own WhatsApp.
            {enrolment.sendLink ? " Or scan the QR code with your phone's camera: it opens WhatsApp with the message ready to send." : null}
          </p>
          <p className="my-4 text-center font-mono text-3xl font-semibold tracking-[0.12em] select-all tabular-nums">
            {enrolment.code}
          </p>
        </div>
        {enrolment.sendLink ? (
          // White behind the code in dark mode too: cameras need the contrast.
          <div className="hidden shrink-0 rounded-md bg-white p-2 sm:block">
            <QRCodeSVG value={enrolment.sendLink} size={128} aria-label="QR code that opens WhatsApp with your code" role="img" />
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {enrolment.sendLink ? (
          <Button asChild size="sm">
            <a href={enrolment.sendLink} target="_blank" rel="noreferrer">
              <ExternalLink className="size-4" />
              Open WhatsApp
            </a>
          </Button>
        ) : null}
        <p className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
          <Spinner className="size-3" />
          Waiting for your message · expires in {minutes}:{seconds}
        </p>
      </div>
    </div>
  );
}

function Notice({ children, tone }: { children: ReactNode; tone?: "warn" }) {
  return (
    <p
      className={
        tone === "warn"
          ? "rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400"
          : "rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground"
      }
    >
      {children}
    </p>
  );
}
